import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Mic, Square, CheckCircle, Loader2, CloudUpload } from "lucide-react";
import { MediaRecorder, register } from "extendable-media-recorder";
import { connect } from "extendable-media-recorder-wav-encoder";
import api from "../../../utils/api";
import SpeakingResultModal from "../../../components/Ielts_test/hasil/speaking/ResultModal";

const partsConfig = [
  {
    part_id: 1,
    title: "Part 1 - Introduction and Interview",
    intro_instruction: "In this part, you will be asked some questions about yourself and familiar topics. Press the record button and answer each question clearly.",
    active_instruction: "Answer the questions clearly. Press the record button to start.",
    time_limit: 240, // 4 menit
  },
  {
    part_id: 2,
    title: "Part 2 - Individual Long Turn (Cue Card)",
    intro_instruction: "You will have to talk about the topic for one to two minutes. You have one minute to think about what you are going to say.",
    active_instruction: "Describe the topic in detail. Talk for 1 to 2 minutes.",
    time_limit: 120, // 2 menit
  },
  {
    part_id: 3,
    title: "Part 3 - Two-Way Discussion",
    intro_instruction: "In this part, you will be asked some further questions connected to the topic in Part 2.",
    active_instruction: "Discuss the topic further based on the questions provided.",
    time_limit: 300, // 5 menit
  }
];

let wavEncoderRegistrationPromise = null;

const ensureWavEncoderRegistered = () => {
  if (!wavEncoderRegistrationPromise) {
    wavEncoderRegistrationPromise = connect().then((encoder) => register(encoder));
  }

  return wavEncoderRegistrationPromise;
};

export default function SpeakingSoalPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSession = location.state || {};
  const [sessionId, setSessionId] = useState(initialSession.sessionId || null);
  const [questions, setQuestions] = useState(initialSession.questions || null);
  const [instructions, setInstructions] = useState(initialSession.instructions || []);
  const [isHydratingSession, setIsHydratingSession] = useState(!initialSession.sessionId || !initialSession.questions);

  const [currentPart, setCurrentPart] = useState(0);
  const [showIntro, setShowIntro] = useState(true);
  const [answers, setAnswers] = useState({}); // { questionId: { blob, audioUrl } }

  // Timer state per part
  const [timeLeft, setTimeLeft] = useState(() => {
    if (instructions && Array.isArray(instructions)) {
      const firstInstruction = instructions.find(inst => String(inst.part) === "1");
      if (firstInstruction) {
        return firstInstruction.preparation_time || 60;
      }
    }
    return 60;
  });
  const [timerStarted, setTimerStarted] = useState(false);
  const [isTimeUp, setIsTimeUp] = useState(false);
  const [isRunning, setIsRunning] = useState(true);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingLevel, setRecordingLevel] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const animationFrameRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  // Ref untuk melacak durasi rekaman secara real-time (menghindari stale closure)
  const recordingTimeRef = useRef(0);
  const recordingStartedAtRef = useRef(null);
  const activeQuestionIdRef = useRef(null);
  const maxRecordingLevelRef = useRef(0);

  // Upload state per question: 'idle' | 'uploading' | 'success' | 'error'
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadErrors, setUploadErrors] = useState({});
  // recording_id dari backend per soal
  const [recordingIds, setRecordingIds] = useState({});

  // Submit & polling state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sessionStatus, setSessionStatus] = useState('in_progress');
  const [showPollingOverlay, setShowPollingOverlay] = useState(false);
  const [evaluationResult, setEvaluationResult] = useState(null);

  // UI state
  const [showResultModal, setShowResultModal] = useState(false);
  const [activeQuestionId, setActiveQuestionId] = useState(null);

  const partConfig = partsConfig[currentPart];

  useEffect(() => {
    if (sessionId && questions) {
      setIsHydratingSession(false);
      return;
    }

    let isMounted = true;

    const hydrateSession = async () => {
      setIsHydratingSession(true);
      try {
        const endpoint = sessionId ? `/speaking/sessions/${sessionId}` : '/speaking/sessions/current';
        const { data } = await api.get(endpoint);
        const sessionData = data?.data;

        if (!isMounted || !data?.success || !sessionData?.session_id) return;

        setSessionId(sessionData.session_id);
        setSessionStatus(sessionData.status || 'in_progress');
        setQuestions(sessionData.questions || null);
        setInstructions(Array.isArray(sessionData.instructions) ? sessionData.instructions : []);

        if (Array.isArray(sessionData.recordings)) {
          const restoredAnswers = {};
          const restoredUploadStatus = {};
          const restoredRecordingIds = {};

          sessionData.recordings.forEach((recording) => {
            if (!recording.question_id) return;
            restoredAnswers[recording.question_id] = { persisted: true };
            restoredUploadStatus[recording.question_id] = 'success';
            if (recording.recording_id) {
              restoredRecordingIds[recording.question_id] = recording.recording_id;
            }
          });

          setAnswers(restoredAnswers);
          setUploadStatus(restoredUploadStatus);
          setRecordingIds(restoredRecordingIds);
        }
      } catch (err) {
        console.error('Failed to restore speaking session:', err);
        if (isMounted) {
          navigate('/speaking-test', { replace: true });
        }
      } finally {
        if (isMounted) {
          setIsHydratingSession(false);
        }
      }
    };

    hydrateSession();

    return () => {
      isMounted = false;
    };
  }, [sessionId, questions, navigate]);

  const getInstructionForPart = (partIdx) => {
    if (!instructions || !Array.isArray(instructions)) return null;
    const partStr = String(partIdx + 1); // "1", "2", "3"
    return instructions.find(inst => String(inst.part) === partStr);
  };

  const dbPartInstruction = getInstructionForPart(currentPart);

  const getQuestionsForPart = (partIdx) => {
    if (!questions) return [];
    if (partIdx === 0) return questions.part1 || [];
    if (partIdx === 1) return questions.part2 || [];
    if (partIdx === 2) return questions.part3 || [];
    return [];
  };

  const currentQuestions = getQuestionsForPart(currentPart);

  // Reset/run timer per part
  useEffect(() => {
    if (showIntro) {
      setTimeLeft(dbPartInstruction?.preparation_time || 60);
    } else {
      const firstQuestion = currentQuestions[0];
      const activeDuration = firstQuestion?.max_duration || partConfig?.time_limit || 240;
      setTimeLeft(activeDuration);
    }
    setTimerStarted(false);
    setIsTimeUp(false);
  }, [currentPart, showIntro, partConfig, dbPartInstruction, currentQuestions]);

  // Upload recording via backend. Backend yang menyimpan file ke storage private/R2.
  const uploadRecording = useCallback(async (questionId, audioBlob, durationSec) => {
    setUploadStatus(prev => ({ ...prev, [questionId]: 'uploading' }));
    try {
      if (!sessionId) {
        throw new Error('Sesi speaking belum siap. Silakan refresh dan coba lagi.');
      }

      if (!audioBlob || audioBlob.size === 0) {
        throw new Error('Rekaman kosong. Pastikan mikrofon menangkap suara, lalu rekam ulang.');
      }

      const formData = new FormData();
      formData.append('session_id', sessionId);
      formData.append('question_id', questionId);
      formData.append('duration', durationSec);
      const extension = audioBlob.type.includes('mp4') ? 'mp4'
        : audioBlob.type.includes('ogg') ? 'ogg'
          : audioBlob.type.includes('wav') ? 'wav'
            : 'webm';
      formData.append('audio', audioBlob, `q${questionId}_${Date.now()}.${extension}`);

      const { data } = await api.post('/speaking/recordings', formData);

      setRecordingIds(prev => ({ ...prev, [questionId]: data.data.recording_id }));
      setUploadStatus(prev => ({ ...prev, [questionId]: 'success' }));
      setUploadErrors(prev => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });
    } catch (err) {
      console.error('Upload recording error:', err);
      setUploadStatus(prev => ({ ...prev, [questionId]: 'error' }));
      setUploadErrors(prev => ({
        ...prev,
        [questionId]: err.response?.data?.message || err.message || 'Gagal unggah. Coba rekam ulang.',
      }));
    }
  }, [sessionId]);

  const cleanupRecordingResources = useCallback(() => {
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    analyserRef.current = null;
    dataArrayRef.current = null;
  }, []);

  const stopRecording = useCallback(() => {
    if (!isRecording) return;

    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }

    setIsRecording(false);
    setRecordingLevel(0);
  }, [isRecording]);

  useEffect(() => {
    if (showIntro || !isRunning || !timerStarted || isTimeUp) {
      return;
    }

    if (timeLeft <= 0) {
      setIsTimeUp(true);
      if (isRecording) {
        stopRecording();
      }
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [showIntro, currentPart, isRunning, timerStarted, timeLeft, isTimeUp, isRecording, stopRecording]);

  // Countdown untuk Waktu Persiapan (Intro)
  useEffect(() => {
    if (!showIntro || !isRunning || isTimeUp || timeLeft <= 0) {
      return;
    }

    const introTimer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(introTimer);
  }, [showIntro, isRunning, timeLeft, isTimeUp]);

  // Otomatis transisi ke active test jika waktu persiapan habis
  useEffect(() => {
    if (showIntro && timeLeft <= 0) {
      setShowIntro(false);
    }
  }, [timeLeft, showIntro]);

  const answersRef = useRef(answers);
  answersRef.current = answers;

  // Clean up Object URLs only when component unmounts
  useEffect(() => {
    return () => {
      cleanupRecordingResources();
      Object.values(answersRef.current).forEach((ans) => {
        if (ans?.audioUrl) {
          try {
            URL.revokeObjectURL(ans.audioUrl);
          } catch (e) { }
        }
      });
    };
  }, [cleanupRecordingResources]);

  const startRecording = useCallback(async (questionId) => {
    if (isTimeUp || isRecording) return;

    if (!navigator.mediaDevices?.getUserMedia) {
      alert("Browser tidak mendukung akses mikrofon. Pastikan halaman dibuka melalui HTTPS.");
      return;
    }

    try {
      await ensureWavEncoderRegistered();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      streamRef.current = stream;
      audioContextRef.current = audioContext;

      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.7;
      source.connect(analyser);

      sourceRef.current = source;
      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.fftSize);

      const updateRecordingLevel = () => {
        const dataArray = dataArrayRef.current;
        const currentAnalyser = analyserRef.current;

        if (!dataArray || !currentAnalyser) return;

        currentAnalyser.getByteTimeDomainData(dataArray);
        let sumSquares = 0;

        for (let i = 0; i < dataArray.length; i += 1) {
          const normalized = (dataArray[i] - 128) / 128;
          sumSquares += normalized * normalized;
        }

        const rms = Math.sqrt(sumSquares / dataArray.length);
        const level = Math.min(100, Math.round(rms * 320));
        maxRecordingLevelRef.current = Math.max(maxRecordingLevelRef.current, level);
        setRecordingLevel(level);
        animationFrameRef.current = requestAnimationFrame(updateRecordingLevel);
      };

      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/wav' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      activeQuestionIdRef.current = questionId;
      maxRecordingLevelRef.current = 0;
      setActiveQuestionId(questionId);
      setUploadStatus(prev => ({ ...prev, [questionId]: 'idle' }));
      setUploadErrors(prev => {
        const next = { ...prev };
        delete next[questionId];
        return next;
      });

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const recordedQuestionId = activeQuestionIdRef.current;
        const chunks = [...audioChunksRef.current];
        const elapsedSeconds = recordingStartedAtRef.current
          ? Math.round((Date.now() - recordingStartedAtRef.current) / 1000)
          : recordingTimeRef.current;
        const durationSec = Math.max(1, elapsedSeconds);

        cleanupRecordingResources();
        mediaRecorderRef.current = null;
        audioChunksRef.current = [];
        setActiveQuestionId(null);
        setRecordingLevel(0);
        activeQuestionIdRef.current = null;
        recordingStartedAtRef.current = null;

        if (!recordedQuestionId) return;

        if (!chunks.length || maxRecordingLevelRef.current <= 5) {
          setUploadStatus(prev => ({ ...prev, [recordedQuestionId]: 'error' }));
          setUploadErrors(prev => ({
            ...prev,
            [recordedQuestionId]: 'Rekaman terlalu hening. Pastikan mikrofon menangkap suara, lalu rekam ulang.',
          }));
          alert('Rekaman terlalu hening. Pastikan mikrofon menangkap suara, lalu rekam ulang.');
          return;
        }

        const audioBlob = new Blob(chunks, { type: 'audio/wav' });
        const audioUrl = URL.createObjectURL(audioBlob);

        setAnswers((prev) => {
          if (prev[recordedQuestionId]?.audioUrl) {
            URL.revokeObjectURL(prev[recordedQuestionId].audioUrl);
          }

          return {
            ...prev,
            [recordedQuestionId]: { blob: audioBlob, audioUrl, mimeType: 'audio/wav' }
          };
        });

        uploadRecording(recordedQuestionId, audioBlob, durationSec);
      };

      setIsRecording(true);
      setRecordingTime(0);
      setRecordingLevel(0);
      recordingTimeRef.current = 0;
      recordingStartedAtRef.current = Date.now();
      recordingIntervalRef.current = setInterval(() => {
        recordingTimeRef.current += 1;
        setRecordingTime(recordingTimeRef.current);
      }, 1000);
      updateRecordingLevel();
      mediaRecorder.start(1000);

      if (!timerStarted) {
        setTimerStarted(true);
      }

    } catch (err) {
      console.error("Error accessing microphone:", err);
      cleanupRecordingResources();
      alert("Akses mikrofon gagal. Izinkan akses mikrofon dan pastikan perangkat mikrofon aktif.");
    }
  }, [cleanupRecordingResources, isRecording, isTimeUp, timerStarted, uploadRecording]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setIsRunning(false);
      if (Object.values(uploadStatus).some(s => s === 'error')) {
        alert('Ada rekaman yang gagal diunggah. Silakan rekam ulang jawaban tersebut sebelum submit.');
        return;
      }

      if (!sessionId) {
        throw new Error('Sesi speaking belum siap. Silakan refresh dan coba lagi.');
      }

      await api.post(`/speaking/sessions/${sessionId}/submit`);
      setSessionStatus('submitted');
      setShowPollingOverlay(true);
    } catch (err) {
      setIsRunning(true);
      const status = err.response?.status;
      if (status === 422) {
        alert('Masih ada soal yang belum terekam. Silakan lengkapi semua jawaban terlebih dahulu.');
      } else {
        alert(err.response?.data?.message || err.message || 'Gagal mengirim sesi. Coba lagi.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fase 4: Polling status sesi tiap 5 detik ke GET /api/speaking/sessions/{id}
  // Fase 5: Setelah completed, fetch hasil lengkap dari GET /api/speaking/results/{id}
  useEffect(() => {
    if (sessionStatus !== 'submitted') return;

    const POLLING_TIMEOUT_MS = 10 * 60 * 1000; // 10 menit
    const startTime = Date.now();

    const pollingInterval = setInterval(async () => {
      // Timeout 10 menit tanpa perubahan status
      if (Date.now() - startTime > POLLING_TIMEOUT_MS) {
        clearInterval(pollingInterval);
        setShowPollingOverlay(false);
        alert('Proses evaluasi membutuhkan waktu lebih lama dari biasanya. Silakan refresh halaman untuk mengecek hasilnya.');
        return;
      }

      try {
        // Fase 4: Poll status sesi (selalu 200, tidak 422)
        const { data: sessionData } = await api.get(`/speaking/sessions/${sessionId}`);
        const status = sessionData.data?.status;

        if (status === 'completed') {
          clearInterval(pollingInterval);

          // Fase 5: Fetch hasil lengkap hanya saat status sudah completed
          try {
            const { data: resultData } = await api.get(`/speaking/results/${sessionId}`);
            setEvaluationResult(resultData.data);
          } catch (resultErr) {
            console.error('Gagal mengambil hasil evaluasi:', resultErr);
          }

          setShowPollingOverlay(false);
          setShowResultModal(true);

        } else if (status === 'failed') {
          clearInterval(pollingInterval);
          setShowPollingOverlay(false);
          alert('Evaluasi AI gagal diproses. Silakan hubungi support. Attempt kamu tidak berkurang.');
        }
        // status 'submitted' atau 'processing' → lanjut polling
      } catch (err) {
        console.error('Polling error:', err);
        // Tetap lanjut polling meski ada error sementara (network fluke)
      }
    }, 5000);

    return () => clearInterval(pollingInterval);
  }, [sessionStatus, sessionId]);

  if (isHydratingSession) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#1e3e7e] font-poppins font-semibold">
          <Loader2 size={20} className="animate-spin" />
          Memuat sesi speaking...
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="bg-white rounded-t-xl shadow-md p-4 mb-2 text-center">
          <h1 className="font-anton text-2xl md:text-3xl text-[#1e3e7e]">
            SPEAKING TEST
          </h1>
          <p className="text-sm text-gray-600 mt-1 font-poppins">
            {showIntro
              ? `Sisa Waktu Persiapan: ${formatTime(timeLeft)}`
              : !timerStarted
                ? "Sisa Waktu: Belum Mulai (Mulai merekam untuk menjalankan timer)"
                : `Sisa Waktu Part: ${formatTime(timeLeft)}`}
          </p>
        </div>

        {showIntro ? (
          /* TAMPILAN INTRO PART */
          <div className="bg-white rounded-b-xl shadow-md p-6 md:p-12 mb-6 flex flex-col items-center text-center">
            <div className="max-w-2xl text-center space-y-6">
              <h2 className="text-2xl md:text-3xl font-bold font-poppins text-[#1e3e7e]">
                {dbPartInstruction?.title || partConfig?.title}
              </h2>
              <div className="bg-blue-50 border-l-4 border-[#1e3e7e] p-6 rounded-r-xl text-left shadow-sm">
                <p className="font-poppins font-bold text-[#1e3e7e] mb-2">Petunjuk Part Ini:</p>
                <p className="font-poppins text-gray-700 leading-relaxed whitespace-pre-line text-sm md:text-base">
                  {dbPartInstruction?.main_instruction || partConfig?.intro_instruction}
                </p>
              </div>
            </div>
          </div>
        ) : (
          /* TAMPILAN ACTIVE TEST */
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
            {/* Instructions Block */}
            <div className="lg:col-span-1 space-y-4">
              <div className="bg-white rounded-b-xl lg:rounded-xl shadow-md p-5">
                <h2 className="font-anton text-xl text-[#1e3e7e] mb-3">
                  {dbPartInstruction?.title || partConfig?.title}
                </h2>
                <div className="bg-blue-50 border-l-4 border-[#1e3e7e] p-4 rounded text-sm font-poppins text-gray-700 leading-relaxed space-y-1.5">
                  {dbPartInstruction && Array.isArray(dbPartInstruction.sub_instructions) ? (
                    dbPartInstruction.sub_instructions.map((sub, idx) => (
                      <p key={idx} className="flex items-start gap-1">
                        <span className="text-[#1e3e7e] font-bold">•</span>
                        <span>{sub}</span>
                      </p>
                    ))
                  ) : (
                    partConfig?.active_instruction
                  )}
                </div>
              </div>
            </div>

            {/* Questions & Recording Area */}
            <div className="lg:col-span-2 space-y-4">
              {isTimeUp && (
                <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-xl shadow-sm font-poppins text-sm">
                  <p className="font-bold">Waktu pengerjaan Part ini telah habis!</p>
                  <p className="text-xs mt-1">
                    Silakan lanjut ke bagian berikutnya dengan menekan tombol <strong>"Next Part"</strong> di bawah (atau <strong>"Submit Test"</strong> jika sudah di Part 3).
                  </p>
                </div>
              )}
              <div className="bg-white rounded-xl shadow-md p-5 max-h-[70vh] overflow-y-auto space-y-8">
                {currentQuestions.map((q, idx) => {
                  const isCurrentlyRecording = isRecording && activeQuestionId === q.id;
                  const hasAnswer = !!answers[q.id];

                  return (
                    <div key={q.id} className="p-5 border-2 border-gray-200 rounded-xl hover:border-[#1e3e7e] transition duration-300 shadow-sm">
                      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">

                        {/* Question Text */}
                        <div className="flex-1 w-full">
                          <p className="font-poppins font-semibold text-[#1e3e7e] mb-2 text-lg whitespace-pre-line">
                            <span className="text-yellow-500 mr-2">Q{idx + 1}:</span>
                            {q.instruction}
                          </p>

                          {/* Cue Card bullets if it is Part 2 */}
                          {currentPart === 1 && q.cue_card_bullets && Array.isArray(q.cue_card_bullets) && (
                            <ul className="list-disc list-inside mt-2 pl-4 text-gray-700 space-y-1 text-sm font-poppins">
                              {q.cue_card_bullets.map((bullet, bIdx) => (
                                <li key={bIdx}>{bullet}</li>
                              ))}
                            </ul>
                          )}

                          {hasAnswer && !isCurrentlyRecording && (
                            <div className="mt-2 space-y-1">
                              <div className="flex items-center text-green-600 text-sm font-semibold">
                                <CheckCircle size={16} className="mr-1" />
                                Recorded
                              </div>
                              {/* Upload status indicator */}
                              {uploadStatus[q.id] === 'uploading' && (
                                <div className="flex items-center gap-1.5 text-blue-500 text-xs font-poppins">
                                  <Loader2 size={12} className="animate-spin" />
                                  Mengunggah rekaman...
                                </div>
                              )}
                              {uploadStatus[q.id] === 'success' && (
                                <div className="flex items-center gap-1.5 text-green-500 text-xs font-poppins">
                                  <CloudUpload size={12} />
                                  Rekaman tersimpan
                                </div>
                              )}
                              {uploadStatus[q.id] === 'error' && (
                                <div className="flex items-center gap-1.5 text-red-500 text-xs font-poppins">
                                  <span aria-hidden="true">!</span>
                                  <span>{uploadErrors[q.id] || 'Gagal unggah. Coba rekam ulang.'}</span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Recording Controls */}
                        <div className="flex flex-col items-center min-w-[120px]">
                          {isCurrentlyRecording ? (
                            <div className="flex flex-col items-center">
                              <button
                                onClick={stopRecording}
                                className="w-14 h-14 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center text-white shadow-lg animate-pulse"
                              >
                                <Square fill="currentColor" size={24} />
                              </button>
                              <span className="text-red-500 font-bold mt-2 text-sm">
                                {formatTime(recordingTime)}
                              </span>
                              <div className="mt-2 h-1.5 w-24 overflow-hidden rounded-full bg-red-100">
                                <div
                                  className="h-full rounded-full bg-red-500 transition-all duration-75"
                                  style={{ width: `${recordingLevel}%` }}
                                />
                              </div>
                              <span className="mt-1 text-[10px] font-poppins text-gray-500">
                                {recordingLevel > 5 ? 'Suara terdeteksi' : 'Belum ada suara'}
                              </span>
                            </div>
                          ) : (
                            <button
                              onClick={() => startRecording(q.id)}
                              disabled={isRecording || isTimeUp}
                              className={`w-14 h-14 rounded-full flex items-center justify-center shadow-md transition ${isRecording || isTimeUp
                                ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                : 'bg-[#1e3e7e] hover:bg-blue-800 text-white'
                                }`}
                              title={isTimeUp ? "Waktu telah habis" : hasAnswer ? "Re-record answer" : "Start recording"}
                            >
                              <Mic size={24} />
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Audio Player if recorded */}
                      {hasAnswer && !isCurrentlyRecording && answers[q.id]?.audioUrl && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <audio
                            src={answers[q.id].audioUrl}
                            controls
                            className="w-full h-10"
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
          <button
            onClick={() => {
              if (!showIntro) {
                // If in active test, go back to Intro first
                setShowIntro(true);
              } else {
                // If in Intro, go back to active test of previous part
                setCurrentPart(Math.max(0, currentPart - 1));
                setShowIntro(false);
              }
            }}
            disabled={(currentPart === 0 && showIntro) || isRecording}
            className="px-6 py-2 bg-gray-200 text-gray-700 rounded-xl font-poppins disabled:opacity-50 hover:bg-gray-300 w-full sm:w-auto transition"
          >
            Previous Part
          </button>

          <div className="text-sm font-poppins text-gray-600 font-semibold">
            Part {currentPart + 1} of {partsConfig.length} {showIntro ? "(Intro)" : "(Mengerjakan)"}
          </div>

          {showIntro ? (
            <button
              onClick={() => setShowIntro(false)}
              className="px-6 py-2 bg-[#1e3e7e] text-white rounded-xl font-poppins font-semibold hover:bg-blue-800 w-full sm:w-auto transition shadow-md"
            >
              Mulai Part {currentPart + 1}
            </button>
          ) : currentPart < partsConfig.length - 1 ? (
            <button
              onClick={() => {
                setCurrentPart(currentPart + 1);
                setShowIntro(true);
              }}
              disabled={isRecording}
              className="px-6 py-2 bg-[#1e3e7e] text-white rounded-xl font-poppins font-semibold hover:bg-blue-800 disabled:opacity-50 w-full sm:w-auto transition shadow-md"
            >
              Next Part
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isRecording || isSubmitting || Object.values(uploadStatus).some(s => s === 'uploading')}
              className="px-8 py-2 bg-yellow-400 text-[#1e3e7e] rounded-xl font-poppins font-bold hover:scale-105 hover:bg-yellow-500 disabled:opacity-50 w-full sm:w-auto transition shadow-md flex items-center justify-center gap-2"
            >
              {isSubmitting ? (
                <><Loader2 size={16} className="animate-spin" /> Mengirim...</>
              ) : (
                'Submit Test'
              )}
            </button>
          )}
        </div>
      </div>

      {/* AI Polling Overlay */}
      {showPollingOverlay && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 text-center max-w-sm mx-4 shadow-2xl">
            <div className="flex justify-center mb-4">
              <Loader2 size={48} className="animate-spin text-[#1e3e7e]" />
            </div>
            <h3 className="font-bold font-poppins text-[#1e3e7e] text-lg mb-2">
              AI Sedang Menganalisis Jawaban Kamu...
            </h3>
            <p className="text-gray-500 text-sm font-poppins">
              Hasilnya akan siap dalam beberapa menit. Mohon jangan tutup halaman ini.
            </p>
          </div>
        </div>
      )}

      {/* Result Modal */}
      {showResultModal && (
        <SpeakingResultModal
          answers={answers}
          evaluationResult={evaluationResult}
          recordingIds={recordingIds}
          questions={questions}
          onClose={() => {
            setShowResultModal(false);
            navigate('/IELTSTest');
          }}
        />
      )}
    </>
  );
}
